// frontend/src/pages/Ajustes.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useAppBackground } from '../context/AppBackgroundContext';
import networkService from '../services/networkService';
import './Ajustes.css';
import { useNotification } from '../context/NotificationContext';
import CharacterClassSelector from '../components/CharacterClassSelector';
import LanguageSelector from '../components/LanguageSelector';
import { useI18n } from '../i18n';
import { setPreferredLanguage } from '../api';
import {
  MAIN_CORE_DISPLAY_MODE_EVENT,
  getMainCoreDisplayMode,
  setMainCoreDisplayMode as persistMainCoreDisplayMode
} from '../utils/mainCoreDisplayMode';

function getCyberpunkPreference() {
  try {
    const stored = localStorage.getItem('cyberpunkMode');
    return stored === null ? true : stored === 'true';
  } catch (e) {
    return true;
  }
}

function getSolarpunkPreference() {
  try {
    const stored = localStorage.getItem('solarpunkMode');
    return stored === 'true';
  } catch (e) {
    return false;
  }
}

function stripStrongMarkup(value) {
  return String(value || '').replace(/<\/?strong>/g, '');
}

function renderStrongInterpolation(template, marker, value) {
  const text = String(template || '');
  const markerIndex = text.indexOf(marker);
  if (markerIndex === -1) return stripStrongMarkup(text);

  const before = text.slice(0, markerIndex);
  const after = text.slice(markerIndex + marker.length);
  return (
    <>
      {stripStrongMarkup(before)}
      <strong>{value}</strong>
      {stripStrongMarkup(after)}
    </>
  );
}

// Categorías de emojis
const EMOJI_CATEGORIES = {
  'Animales': ['🐱','🐶','🦊','🐻','🐼','🐸','🦁','🐨','🐯','🐵', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', 'm','🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷', '🦂', '🐢', '🐍', '🦎', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🦣', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🦬', '🐃', '🐂', '🐄', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛', '🐓', '🦃', '🦚', '🦜', '🦢', '🦩', '🕊', '🐇', '🦝', '🦨', '🦡', '🦫', '🦦', '🦥', '🐁', '🐀', '🐿', '🦔'],
  'Caras': ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🥸', '🤩', '🥳', '😏', '😒', '😞', '😔', 'worried', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃'],
  'Objetos': ['⌚', '📱', '📲', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️', '🕹️', '🗜️', '💽', '💾', '💿', '📀', '📼', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📞', '☎️', '📟', 'Fax', '📺', '📻', '🎙️', '🎚️', '🎛️', '🧭', '⏱️', '⏲️', '⏰', '🕰️', '⌛', '⏳', '📡', '🔋', '🔌', '💡', '🔦', '🕯️', '🪔', '🧯', '🛢️', '💸', '💵', '💴', '💶', '💷', '💰', '💳', '💎', '⚖️', '🧰', '🔧', '🔨', '⚒️', '🛠️', '⛏️', '🪚', '🔩', '⚙️', '🪤', '🧱', '⛓️', '🧲', '🔫', '💣', '🧨', '🪓', '🔪', '🗡️', '⚔️', '🛡️', '🚬', '⚰️', '🪦', '⚱️', '🏺', '🔮', '📿', '🧿', '💈', '⚗️', '🔭', '🔬', '🕳️', '🩹', '🩺', '💊', '💉', '🩸', '🧬', '🦠', '🧫', '🧪', '🌡️', '🧹', '🪠', '🧺', '🧻', '🚽', '🚰', '🚿', '🛁', '🛀', '🧼', '🪥', '🪒', '🧽', '🪣', '🧴', '🛎️', '🔑', '🗝️', '🚪', '🪑', '🛋️', '🛏️', '🛌', '🧸', '🪆', '🖼️', '🪞', '🪟', '🛍️', '🛒', '🎁', '🎈', '🎏', '🎀', '🪄', '🪅', '🎊', '🎉', '🎎', '🏮', '🎐', '🧧', '✉️', '📩', '📨', '📧', '💌', '📥', '📤', '📦', '🏷️', '🪧', '📪', '📫', '📬', '📭', '📮', '📯', '📜', '📃', '📄', '📑', '🧾', '📊', '📈', '📉', '🗒️', '🗓️', '📆', '📅', '🗑️', '📇', '🗃️', '🗳️', '🗄️', '📋', '📁', '📂', '🗂️', '🗞️', '📰', '📓', '📔', '📒', '📕', '📗', '📘', '📙', '📚', '📖', '🔖', '🧷', '🔗', '📎', '🖇️', '📐', '📏', '🧮', '📌', '📍', '✂️', '🖊️', '🖋️', '✒️', '🖌️', '🖍️', '📝', '✏️', '🔍', '🔎', '🔏', '🔐', '🔒', '🔓']
};

export default function Ajustes() {
  const { addNotification } = useNotification();
  const { user, updateUserAvatar, updateSphereMode, refreshUser } = useContext(AuthContext);
  const { t } = useI18n();
  const { settings, toggleInvertMainOrder } = useAppBackground() || {};
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [notifEnabled, setNotifEnabled] = useState(user?.audioEnabled ?? false);

  // Account State
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [dynamicNameEnabled, setDynamicNameEnabled] = useState(user?.dynamicNameEnabled ?? true);
  const [savingPreferences, setSavingPreferences] = useState(false);

  // Red Silenciosa State
  const redSilenciosaRef = useRef(null);
  const [buyingTier, setBuyingTier] = useState(null);
  const [qHandle, setQHandle] = useState(user?.qHandle || user?.q_handle || '');
  const [isPublic, setIsPublic] = useState(user?.isPublicProfile || user?.is_public_profile || false);
  const [qPin, setQPin] = useState('');
  const [updatingNetwork, setUpdatingNetwork] = useState(false);

  // Planes/Imágenes Scroll & Highlight
  const planesRef = useRef(null);
  const [highlightPlanes, setHighlightPlanes] = useState(false);

  // 2FA Modal State
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [authMethod, setAuthMethod] = useState('pin'); // 'pin' | 'otp'
  const [verificationCode, setVerificationCode] = useState('');

  // Password Reset State
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [resetStep, setResetStep] = useState('otp'); // 'otp' | 'reset'
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Sincronizar estado local cuando el usuario se refresca
  useEffect(() => {
    if (user) {
      setQHandle(user.qHandle || user.q_handle || '');
      setIsPublic(user.isPublicProfile || user.is_public_profile || false);
      setDisplayName(user.displayName || '');
      setDynamicNameEnabled(user.dynamicNameEnabled ?? true);
    }
  }, [user]);

  // Ref for auto-scrolling
  const emojiGridRef = useRef(null);

  // Estado local para Dark Mode
  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem('darkMode');
      if (stored === 'true') return true;
      if (stored === 'false') return false;
      return document.documentElement.getAttribute('data-theme') === 'dark';
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('darkMode', isDark ? 'true' : 'false');
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    } catch (e) {}
  }, [isDark]);

  const [isCyberpunk, setIsCyberpunk] = useState(() => getCyberpunkPreference());
  const [isSolarpunk, setIsSolarpunk] = useState(() => getSolarpunkPreference());
  const [mainCoreDisplayMode, setMainCoreDisplayModeState] = useState(() => getMainCoreDisplayMode());

  useEffect(() => {
    const value = isCyberpunk ? 'true' : 'false';
    document.documentElement.setAttribute('data-cyberpunk', value);
    localStorage.setItem('cyberpunkMode', value);
    window.dispatchEvent(new CustomEvent('q-cyberpunk-mode-change', { detail: isCyberpunk }));
  }, [isCyberpunk]);

  useEffect(() => {
    const value = isSolarpunk ? 'true' : 'false';
    document.documentElement.setAttribute('data-solarpunk', value);
    localStorage.setItem('solarpunkMode', value);
    window.dispatchEvent(new CustomEvent('q-solarpunk-mode-change', { detail: isSolarpunk }));
  }, [isSolarpunk]);

  useEffect(() => {
    const syncCyberpunkMode = (e) => {
      if (e.detail !== undefined) setIsCyberpunk(e.detail);
    };
    const syncSolarpunkMode = (e) => {
      if (e.detail !== undefined) setIsSolarpunk(e.detail);
    };
    window.addEventListener('q-cyberpunk-mode-change', syncCyberpunkMode);
    window.addEventListener('q-solarpunk-mode-change', syncSolarpunkMode);
    return () => {
      window.removeEventListener('q-cyberpunk-mode-change', syncCyberpunkMode);
      window.removeEventListener('q-solarpunk-mode-change', syncSolarpunkMode);
    };
  }, []);

  useEffect(() => {
    const syncMainCoreDisplayMode = () => setMainCoreDisplayModeState(getMainCoreDisplayMode());
    window.addEventListener(MAIN_CORE_DISPLAY_MODE_EVENT, syncMainCoreDisplayMode);
    window.addEventListener('storage', syncMainCoreDisplayMode);
    return () => {
      window.removeEventListener(MAIN_CORE_DISPLAY_MODE_EVENT, syncMainCoreDisplayMode);
      window.removeEventListener('storage', syncMainCoreDisplayMode);
    };
  }, []);

  const useMainCoreButtons = mainCoreDisplayMode === 'buttons';

  useEffect(() => {
      // Auto-scroll when category opens
      if (selectedCategory && emojiGridRef.current) {
          emojiGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
  }, [selectedCategory]);

  useEffect(() => {
    if (location.hash === '#red-silenciosa' && redSilenciosaRef.current) {
      setTimeout(() => {
        redSilenciosaRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300); // Pequeño delay para asegurar el renderizado
    } else if (location.hash === '#planes-imagenes' && planesRef.current) {
      setTimeout(() => {
        planesRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightPlanes(true);
        // Desactivar highlight después de 2.5s
        setTimeout(() => setHighlightPlanes(false), 2500);
      }, 300);
    }
  }, [location]);

  const handleChooseAvatar = async (avatar) => {
    try {
      updateUserAvatar(avatar); // Actualización optimista de la UI
      const api = await import('../api').then(m => m.default).catch(() => null);
      if (api) await api.put('/users/avatar', { avatar });
    } catch (err) {
      console.error('Error guardando el avatar en la DB:', err);
    }
    setSelectedCategory(null);
  };

  const handleNotifToggle = async () => {
    const next = !notifEnabled;
    setNotifEnabled(next);
    // Ideal: llamar API PUT /users/audio-enabled
    try {
      const api = await import('../api').then(m => m.default).catch(() => null);
      if (api) await api.put('/users/audio-enabled', { audioEnabled: !!next });
    } catch (err) {
      console.warn('No se pudo persistir la preferencia de notificaciones', err);
    }
  };

  const handleSphereModeChange = async (mode) => {
    updateSphereMode(mode);
    try {
      const api = await import('../api').then(m => m.default).catch(() => null);
      if (api) await api.put('/users/sphere-mode', { sphereMode: mode });
    } catch (err) {
      console.warn('No se pudo persistir el modo de esfera', err);
    }
    navigate('/'); // Redirigir al Main/Dashboard para ver el cambio
  };

  const handleMainCoreDisplayModeToggle = () => {
    const nextMode = useMainCoreButtons ? 'sphere' : 'buttons';
    const persisted = persistMainCoreDisplayMode(nextMode);
    setMainCoreDisplayModeState(persisted);
  };

  const handleLanguageChange = async (language) => {
    try {
      await setPreferredLanguage(language);
      addNotification(t('language.saved'), 'success');
      if (refreshUser) await refreshUser();
    } catch (err) {
      addNotification(t('language.saveFailed'), 'error');
    }
  };

  const handleRequestOtp = async () => {
    try {
      await networkService.sendPinOtp();
      addNotification(t('settings.otpSent'), 'success');
      setAuthMethod('otp');
      setVerificationCode('');
    } catch (err) {
      addNotification(err.response?.data?.error || t('settings.requestCodeError'), 'error');
    }
  };

  const handleConfirm2FA = async () => {
    setUpdatingNetwork(true);
    try {
      const payload = { qHandle, isPublicProfile: isPublic, qPin };
      if (authMethod === 'pin') {
        payload.currentPin = verificationCode;
      } else {
        payload.otp = verificationCode;
      }

      await networkService.updateNetworkProfile(payload);
      addNotification(t('settings.networkSavedFull'), 'success');

      setQPin('');
      setVerificationCode('');
      setShow2FAModal(false);
      navigate('/network');

      if (refreshUser) await refreshUser();
    } catch (err) {
      addNotification(err.response?.data?.error || t('settings.verifyError'), 'error');
    } finally {
      setUpdatingNetwork(false);
    }
  };

  const handleStartPasswordReset = async () => {
    setResetLoading(true);
    try {
      const api = await import('../api').then(m => m.default);
      await api.post('/auth/forgot-password', { email: user.email });
      setResetStep('otp');
      setShowPasswordResetModal(true);
    } catch (err) {
      addNotification(err.response?.data?.error || t('settings.requestCodeError'), 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerifyResetOtp = (e) => {
    e.preventDefault();
    if (resetOtp.length === 4) {
      setResetStep('reset');
    }
  };

  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      addNotification(t('auth.passwordMismatch'), 'error');
      return;
    }
    setResetLoading(true);
    try {
      const api = await import('../api').then(m => m.default);
      await api.post('/auth/reset-password', {
        email: user.email,
        otp: resetOtp,
        newPassword
      });
      addNotification(t('settings.passwordUpdated'), 'success');
      setShowPasswordResetModal(false);
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      addNotification(err.response?.data?.error || t('settings.passwordUpdateError'), 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleUpdatePreferences = async () => {
    if (!dynamicNameEnabled && (!displayName || displayName.trim().length < 2)) {
      addNotification(t('settings.displayNameRequired'), 'info');
      return;
    }

    setSavingPreferences(true);
    try {
      const api = await import('../api').then(m => m.default).catch(() => null);
      if (api) {
        await api.put('/users/me/preferences', {
          dynamicNameEnabled,
          displayName: displayName.trim() || null
        });
        addNotification(t('settings.preferencesSaved'), 'success');
        if (refreshUser) await refreshUser();
      }
    } catch (err) {
      addNotification(err.response?.data?.error || t('settings.preferencesError'), 'error');
    } finally {
      setSavingPreferences(false);
    }
  };

  const handleUpdateNetworkProfile = async () => {
    // Si se intenta establecer un nuevo PIN y el usuario ya tiene uno configurado
    if (qPin && user?.has_pin) {
      setAuthMethod('pin');
      setVerificationCode('');
      setShow2FAModal(true);
      return;
    }

    setUpdatingNetwork(true);
    try {
      const payload = { qHandle, isPublicProfile: isPublic };
      if (qPin) payload.qPin = qPin;

      await networkService.updateNetworkProfile(payload);
      addNotification(t('settings.networkSaved'), 'success');
      setQPin('');
      navigate('/network');

      // Forzar actualización del contexto de usuario
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      addNotification(err.response?.data?.error || t('settings.networkError'), 'error');
    } finally {
      setUpdatingNetwork(false);
    }
  };

  const handleBuyTier = async (tier) => {
    setBuyingTier(tier);
    try {
      const api = await import('../api').then(m => m.default).catch(() => null);
      if (api) {
        const res = await api.post('/payments/create-preference', { tier });
        if (res.data && res.data.init_point) {
          window.location.href = res.data.init_point;
        } else {
          throw new Error(t('settings.noPaymentPoint'));
        }
      }
    } catch (err) {
      console.error('Error al iniciar pago:', err);
      addNotification(err.response?.data?.error || t('settings.paymentStartError'), 'error');
    } finally {
      setBuyingTier(null);
    }
  };

  const renderAvatar = (av) => {
    const cleanAv = av === '/default-avatar.png' ? null : av;
    if (!cleanAv) return '👤';
    if (cleanAv.startsWith('http')) {
      return <img src={cleanAv} alt="avatar" className="conn-avatar-img" referrerPolicy="no-referrer" />;
    }
    return <span className="conn-avatar-emoji">{cleanAv}</span>;
  };

  const categoryLabel = (cat) => ({
    Animales: t('settings.emojiCategories.animals'),
    Caras: t('settings.emojiCategories.faces'),
    Objetos: t('settings.emojiCategories.objects')
  }[cat] || cat);

  return (
    <div id="settings-main" className="ajustes-container" style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '20px' }}>{t('settings.title')}</h2>

      <section className="ajustes-section">
        <h3>{t('language.settingsTitle')}</h3>
        <p className="section-desc">{t('language.settingsDescription')}</p>
        <LanguageSelector onChange={handleLanguageChange} />
      </section>

      {/* --- SECCIÓN APARIENCIA (Modo Claro/Oscuro) --- */}
      <section className="ajustes-section">
        <h3>{t('settings.appearance')}</h3>
        <div
          id="btn-theme-toggle"
          className="theme-toggle-card"
          onClick={() => setIsDark(!isDark)}
          role="button"
          tabIndex={0}
          aria-pressed={isDark}
        >
          <div className="theme-icon">{isDark ? '🌙' : '☀️'}</div>
          <div className="theme-info">
            <span className="theme-title">{isDark ? t('settings.darkMode') : t('settings.lightMode')}</span>
            <span className="theme-desc">
              {isDark ? t('settings.darkDesc') : t('settings.lightDesc')}
            </span>
          </div>
          <div className={`theme-switch ${isDark ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>

        <div
          className="theme-toggle-card cyberpunk-toggle-card"
          style={{ marginTop: '15px' }}
          onClick={() => {
            const next = !isCyberpunk;
            setIsCyberpunk(next);
            if (next) setIsSolarpunk(false);
          }}
          role="button"
          tabIndex={0}
          aria-pressed={isCyberpunk}
          aria-label={t('settings.cyberpunkAria')}
        >
          <div className="theme-icon">#</div>
          <div className="theme-info">
            <span className="theme-title">{t('settings.cyberpunkMode')}</span>
            <span className="theme-desc">{t('settings.cyberpunkDesc')}</span>
          </div>
          <div className={`theme-switch ${isCyberpunk ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>

        <div
          className="theme-toggle-card solarpunk-toggle-card"
          style={{ marginTop: '15px' }}
          onClick={() => {
            const next = !isSolarpunk;
            setIsSolarpunk(next);
            if (next) setIsCyberpunk(false);
          }}
          role="button"
          tabIndex={0}
          aria-pressed={isSolarpunk}
          aria-label={t('settings.solarpunkAria')}
        >
          <div className="theme-icon">🌿</div>
          <div className="theme-info">
            <span className="theme-title">{t('settings.solarpunkMode')}</span>
            <span className="theme-desc">{t('settings.solarpunkDesc')}</span>
          </div>
          <div className={`theme-switch ${isSolarpunk ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>

        <div
          className="theme-toggle-card"
          style={{ marginTop: '15px' }}
          onClick={toggleInvertMainOrder}
          role="button"
          tabIndex={0}
          aria-pressed={settings?.invertMainOrder}
        >
          <div className="theme-icon">🔄</div>
          <div className="theme-info">
            <span className="theme-title">{t('settings.invertHome')}</span>
            <span className="theme-desc">{t('settings.invertHomeDesc')}</span>
          </div>
          <div className={`theme-switch ${settings?.invertMainOrder ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>

        <div
          className="theme-toggle-card"
          style={{ marginTop: '15px' }}
          onClick={handleMainCoreDisplayModeToggle}
          role="button"
          tabIndex={0}
          aria-pressed={useMainCoreButtons}
          aria-label={t('settings.mainCoreButtonsAria')}
        >
          <div className="theme-icon" aria-hidden="true">{useMainCoreButtons ? '|||' : 'O'}</div>
          <div className="theme-info">
            <span className="theme-title">{t('settings.mainCoreButtons')}</span>
            <span className="theme-desc">{t('settings.mainCoreButtonsDesc')}</span>
          </div>
          <div className={`theme-switch ${useMainCoreButtons ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>
      </section>

      {/* --- SECCIÓN MODO DE ESFERAS --- */}
      <section className="ajustes-section">
        <h3>{t('settings.sphereStyle')}</h3>
        <p className="section-desc">{t('settings.sphereStyleDesc')}</p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className={`category-btn ${(user?.sphereMode || 'classic') === 'classic' ? 'selected' : ''}`}
            onClick={() => handleSphereModeChange('classic')}
            style={{ flex: 1 }}
          >
            {t('settings.classic')}
          </button>
          <button
            className={`category-btn ${user?.sphereMode === 'glass' ? 'selected' : ''}`}
            onClick={() => handleSphereModeChange('glass')}
            style={{ flex: 1 }}
          >
            {t('settings.glass')}
          </button>
          <button
            className={`category-btn ${user?.sphereMode === 'wood' ? 'selected' : ''}`}
            onClick={() => handleSphereModeChange('wood')}
            style={{ flex: 1 }}
          >
            {t('settings.wood')}
          </button>
        </div>
      </section>

      {/* --- SECCIÓN AVATAR --- */}
      <section className="ajustes-section">
        <h3>{t('settings.avatarTitle')}</h3>
        <p className="section-desc">{t('settings.avatarDesc')}</p>

        <div className="current-avatar-display">
           <span className="avatar-big">{renderAvatar(user?.avatar || user?.avatar_url)}</span>
           <span className="avatar-label">{t('settings.currentAvatar')}</span>
        </div>

        <div className="avatar-categories">
            {Object.keys(EMOJI_CATEGORIES).map(cat => (
                <button key={cat} className="category-btn" onClick={() => setSelectedCategory(cat)}>
                    {categoryLabel(cat)} 🔽
                </button>
            ))}
        </div>

        {/* Modal/Dropdown de selección */}
        {selectedCategory && (
            <div className="avatar-selection-area" ref={emojiGridRef}>
                <div className="avatar-selection-header">
                    <h4>{categoryLabel(selectedCategory)}</h4>
                    <button className="close-cat-btn" onClick={() => setSelectedCategory(null)}>✖</button>
                </div>
                <div className="avatar-grid" style={{ overflowX: 'hidden' }}>
                    {EMOJI_CATEGORIES[selectedCategory].map((emoji, idx) => (
                        <button
                            key={idx}
                            className={`avatar-option ${user?.avatar === emoji ? 'selected' : ''}`}
                            onClick={() => handleChooseAvatar(emoji)}
                        >
                            {emoji}
                        </button>
                    ))}
                </div>
            </div>
        )}
      </section>

      {/* --- SECCIÓN RED SILENCIOSA --- */}
      <section className="ajustes-section" ref={redSilenciosaRef}>
        <h3>{t('settings.silentNetwork')}</h3>
        <p className="section-desc">{t('settings.silentNetworkDesc')}</p>

        <div className="ajustes-field" style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', opacity: 0.8 }}>Q-Handle</label>
          <input
            type="text"
            value={qHandle}
            onChange={(e) => setQHandle(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
            placeholder="@usuario"
            className="search-input" // Reutilizando estilo si es posible
            style={{ width: '100%', padding: '10px', background: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-color)' }}
          />
        </div>

        <div className="theme-toggle-card" onClick={() => setIsPublic(!isPublic)} style={{ marginBottom: '15px' }}>
          <div className="theme-icon">{isPublic ? '🌐' : '🔒'}</div>
          <div className="theme-info">
            <span className="theme-title">{t('settings.publicProfile')}</span>
            <span className="theme-desc">{t('settings.publicProfileDesc')}</span>
          </div>
          <div className={`theme-switch ${isPublic ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>

        <div className="ajustes-field" style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', opacity: 0.8 }}>{t('settings.newQPin')}</label>
          <input
            type="password"
            maxLength={4}
            value={qPin}
            onChange={(e) => setQPin(e.target.value.replace(/\D/g, ''))}
            placeholder="****"
            style={{ width: '100%', padding: '10px', background: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-color)', letterSpacing: '5px' }}
          />
        </div>

        <button
          className="category-btn selected"
          style={{ width: '100%', marginTop: '10px' }}
          onClick={handleUpdateNetworkProfile}
          disabled={updatingNetwork}
        >
          {updatingNetwork ? t('common.saving') : t('settings.saveNetwork')}
        </button>
      </section>

      {/* --- SECCIÓN PLANES DE IMÁGENES --- */}
      <section className={`ajustes-section ${highlightPlanes ? 'highlight-pulse' : ''}`} id="planes-imagenes" ref={planesRef}>
        <h3>{t('settings.imagePlans')}</h3>
        <p className="section-desc">{t('settings.imagePlansDesc')}</p>

        <div className="user-profile-card plan-card">
          <div className="user-profile-info">
            <span className="user-label">{t('settings.currentPlan')}</span>
            <span className="user-name" style={{ fontSize: '1.2rem', fontWeight: 'bold', textTransform: 'uppercase', color: 'var(--accent-color, #4db6ac)' }}>
              {user?.imagePlan || 'Free'}
            </span>
            <div className="quota-usage" style={{ marginTop: '10px' }}>
              <div className="quota-bar-container" style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden', marginBottom: '5px' }}>
                <div
                  className="quota-bar-fill"
                  style={{
                    width: `${Math.min(100, ((user?.imagesUsedThisMonth || 0) / (user?.imagePlan === 'tier1' ? 120 : (user?.imagePlan === 'tier2' ? 1000 : 7))) * 100)}%`,
                    height: '100%',
                    background: 'var(--accent-color, #4db6ac)',
                    transition: 'width 0.5s ease'
                  }}
                />
              </div>
              <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                {t('settings.imagesUsed', { used: user?.imagesUsedThisMonth || 0, limit: user?.imagePlan === 'tier1' ? 120 : (user?.imagePlan === 'tier2' ? 1000 : 7) })}
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button
              className={`category-btn ${buyingTier === 'tier1' ? 'btn-processing' : ''}`}
              style={{ width: '100%', border: '1px solid gold', color: 'gold', padding: '10px' }}
              onClick={() => handleBuyTier('tier1')}
              disabled={buyingTier !== null}
            >
              {buyingTier === 'tier1' ? t('common.processing') : '120 images ($1000 ARS)'}
            </button>
            <span style={{ marginTop: '8px', fontSize: '0.85rem', opacity: 0.7, fontWeight: 'bold', color: 'gold' }}>Tier 1</span>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button
              className={`category-btn ${buyingTier === 'tier2' ? 'btn-processing' : ''}`}
              style={{ width: '100%', border: '1px solid #e5e4e2', color: '#e5e4e2', padding: '10px' }}
              onClick={() => handleBuyTier('tier2')}
              disabled={buyingTier !== null}
            >
              {buyingTier === 'tier2' ? t('common.processing') : '1000 images ($3000 ARS)'}
            </button>
            <span style={{ marginTop: '8px', fontSize: '0.85rem', opacity: 0.7, fontWeight: 'bold', color: '#e5e4e2' }}>Tier 2</span>
          </div>
        </div>
        <p style={{ fontSize: '0.75rem', opacity: 0.5, marginTop: '10px', textAlign: 'center' }}>
          {t('settings.planSupport')}
        </p>
      </section>

      {/* --- SECCIÓN NOTIFICACIONES --- */}
      <section className="ajustes-section">
        <h3>{t('settings.notificationsAudio')}</h3>
        <p className="section-desc">{t('settings.notificationsAudioDesc')}</p>

        <div
          className="theme-toggle-card"
          onClick={handleNotifToggle}
          role="button"
          tabIndex={0}
          aria-pressed={notifEnabled}
        >
          <div className="theme-icon">{notifEnabled ? '🔊' : '🔇'}</div>
          <div className="theme-info">
            <span className="theme-title">{notifEnabled ? t('settings.audioOn') : t('settings.audioOff')}</span>
          </div>
          <div className={`theme-switch ${notifEnabled ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>

      </section>

      {/* --- SECCIÓN CUENTA & ESTADÍSTICAS --- */}
      <section className="ajustes-section">
        <h3>{t('settings.account')}</h3>

        <div
          className="theme-toggle-card"
          onClick={() => setDynamicNameEnabled(!dynamicNameEnabled)}
          style={{ marginBottom: '20px' }}
        >
          <div className="theme-icon">🎭</div>
          <div className="theme-info">
            <span className="theme-title">{t('settings.dynamicNickname')}</span>
            <span className="theme-desc">{t('settings.dynamicNicknameDesc')}</span>
          </div>
          <div className={`theme-switch ${dynamicNameEnabled ? 'on' : 'off'}`}>
            <div className="switch-handle" />
          </div>
        </div>

        <div className="ajustes-field" style={{ marginBottom: '15px', animation: 'fadeIn 0.3s' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '0.85rem', opacity: 0.8 }}>
            {dynamicNameEnabled
              ? t('settings.baseName')
              : t('settings.displayName')}
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('settings.displayNamePlaceholder')}
            required={!dynamicNameEnabled}
            style={{ width: '100%', padding: '10px', background: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-color)' }}
          />
        </div>

        <button
          className="category-btn selected"
          style={{ width: '100%', marginTop: '5px' }}
          onClick={handleUpdatePreferences}
          disabled={savingPreferences || (!dynamicNameEnabled && !displayName.trim())}
        >
          {savingPreferences ? t('common.saving') : t('settings.saveAccount')}
        </button>

        <button
          className="category-btn"
          style={{ width: '100%', marginTop: '10px', border: '1px solid var(--border-color)' }}
          onClick={handleStartPasswordReset}
          disabled={resetLoading}
        >
          {resetLoading ? t('settings.starting') : t('settings.changePassword')}
        </button>

        <div className="user-profile-card" style={{ marginTop: '20px' }}>
           <div className="user-profile-icon">
               {renderAvatar(user?.avatar || user?.avatar_url)}
           </div>
           <div className="user-profile-info">
               <span className="user-label">{t('settings.loginUser')}</span>
               <span className="user-name" style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--accent-color, #4db6ac)' }}>
                    @{user?.username || '—'}
               </span>
               <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '4px' }}>{user?.email}</div>
           </div>
        </div>

        <div style={{ marginTop: '30px' }}>
            <h4>Clase de Personaje (RPG)</h4>
            <p className="section-desc" style={{ marginBottom: '15px' }}>
                Tu clase determina cómo interactúas con la Red Q.
            </p>
            <CharacterClassSelector
              currentClass={user?.characterClass}
              onSelect={async () => {
                if (refreshUser) await refreshUser();
              }}
            />
        </div>

      </section>

      {/* Modal de Cambio de Contraseña */}
      {showPasswordResetModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-2fa">
            <button className="modal-close" onClick={() => setShowPasswordResetModal(false)}>×</button>
            <h3>{t('settings.passwordModal')}</h3>

            {resetStep === 'otp' ? (
              <form onSubmit={handleVerifyResetOtp}>
                <p className="section-desc">
                  <span>
                    {renderStrongInterpolation(
                      t('settings.otpSentTo', { email: '__QPROJECT_EMAIL__' }),
                      '__QPROJECT_EMAIL__',
                      user?.email || ''
                    )}
                  </span>
                </p>
                <div className="ajustes-field" style={{ textAlign: 'center' }}>
                  <input
                    type="text"
                    maxLength={4}
                    value={resetOtp}
                    onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder="****"
                    className="verification-input"
                    style={{
                      width: '150px',
                      fontSize: '2rem',
                      textAlign: 'center',
                      letterSpacing: '10px',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '8px',
                      color: 'var(--text-color)',
                      padding: '10px'
                    }}
                    autoFocus
                    required
                  />
                </div>
                <div className="modal-actions" style={{ marginTop: '20px' }}>
                  <button
                    id="btn-save-settings" type="submit"
                    className="category-btn selected"
                    style={{ width: '100%' }}
                    disabled={resetOtp.length < 4}
                  >
                    {t('auth.verifyCode')}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleConfirmPasswordReset}>
                <p className="section-desc">{t('settings.enterNewPassword')}</p>
                <div className="ajustes-field">
                  <input
                    type="password"
                    placeholder={t('auth.newPassword')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{ width: '100%', padding: '10px', marginBottom: '10px', background: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-color)' }}
                    required
                    autoFocus
                  />
                  <input
                    type="password"
                    placeholder={t('auth.confirmPassword')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{ width: '100%', padding: '10px', background: 'var(--input-bg)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--text-color)' }}
                    required
                  />
                </div>
                <div className="modal-actions" style={{ marginTop: '20px' }}>
                  <button
                    type="submit"
                    className="category-btn selected"
                    style={{ width: '100%' }}
                    disabled={resetLoading}
                  >
                    {resetLoading ? t('common.updating') : t('settings.confirmChange')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal de Verificación de Identidad (2FA) */}
      {show2FAModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-2fa">
            <button className="modal-close" onClick={() => setShow2FAModal(false)}>×</button>
            <h3>{t('settings.identityVerification')}</h3>
            <p className="section-desc">
              {authMethod === 'pin'
                ? t('settings.pinInstruction')
                : t('settings.otpInstruction')}
            </p>

            <div className="ajustes-field" style={{ textAlign: 'center' }}>
              <input
                type="password"
                maxLength={4}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                placeholder="****"
                className="verification-input"
                style={{
                  width: '150px',
                  fontSize: '2rem',
                  textAlign: 'center',
                  letterSpacing: '10px',
                  background: 'var(--input-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  color: 'var(--text-color)',
                  padding: '10px'
                }}
                autoFocus
              />
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button
                className="category-btn selected"
                style={{ width: '100%' }}
                onClick={handleConfirm2FA}
                disabled={verificationCode.length < 4 || updatingNetwork}
              >
                {updatingNetwork ? t('common.verifying') : t('common.confirm')}
              </button>

              {authMethod === 'pin' && (
                <button
                  className="forgot-pin-btn"
                  onClick={handleRequestOtp}
                  style={{ background: 'none', border: 'none', color: 'var(--accent-color, #4db6ac)', cursor: 'pointer', marginTop: '15px', fontSize: '0.9rem', width: '100%' }}
                >
                  {t('settings.forgotPin')}
                </button>
              )}

              {authMethod === 'otp' && (
                <button
                  className="forgot-pin-btn"
                  onClick={() => setAuthMethod('pin')}
                  style={{ background: 'none', border: 'none', color: 'var(--text-color)', opacity: 0.6, cursor: 'pointer', marginTop: '15px', fontSize: '0.9rem', width: '100%' }}
                >
                  {t('settings.usePinAgain')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
